import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Mail, X } from "lucide-react";
import { NAV_LINKS, BrandMark } from "./Navbar";
import { scrollToId } from "@/lib/scroll";

const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    body: "Information submitted through the contact form on this website (name, email address, subject, and message) is used solely to respond to your enquiry. It is not shared with third parties, not used for marketing, and not stored beyond what is necessary for correspondence. Please do not submit patient reports, medical records, or sensitive health information through this website. This website embeds content from Instagram, which may load third-party resources in your browser.",
  },
  disclaimer: {
    title: "Medical Disclaimer",
    body: "Information on this website is intended for general educational purposes and does not replace an individual medical consultation. Content shared here and through the Little Ones initiative is for awareness only and does not constitute medical advice, diagnosis, or treatment. For any medical concern, please consult a qualified healthcare professional in person. In case of a medical emergency, visit the nearest hospital immediately.",
  },
};

const Footer = () => {
  const [policy, setPolicy] = useState(null);

  return (
    <footer data-testid="site-footer" className="bg-[#49293C] text-[#F6F1EC]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid gap-12 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <BrandMark size={42} />
            <div>
              <p className="font-display text-xl font-semibold leading-tight">Dr. Ojaswini Sheshrao Gajbhiye</p>
              <p className="text-xs text-[#E8C2CB]/80 mt-0.5">Pediatrician · MBBS | DCH | DNB Pediatrics</p>
            </div>
          </div>
          <p className="font-display italic text-lg text-[#E8C2CB] leading-snug">
            "Every child deserves a healthier beginning."
          </p>
        </div>

        <nav aria-label="Footer">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#B89A69] mb-5">Quick Links</p>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.id}>
                <button
                  data-testid={`footer-link-${l.id}`}
                  onClick={() => scrollToId(l.id)}
                  className="text-sm text-[#F6F1EC]/75 hover:text-white transition-colors"
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#B89A69] mb-5">Connect</p>
          <div className="space-y-3">
            <a
              data-testid="footer-email-link"
              href="mailto:ogajbhiye@gmail.com"
              className="flex items-center gap-3 text-sm text-[#F6F1EC]/75 hover:text-white transition-colors"
            >
              <Mail size={16} strokeWidth={1.6} /> ogajbhiye@gmail.com
            </a>
            <a
              data-testid="footer-instagram-link"
              href="https://www.instagram.com/littleonesdrojaswini/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-sm text-[#F6F1EC]/75 hover:text-white transition-colors"
            >
              <Instagram size={16} strokeWidth={1.6} /> @littleonesdrojaswini
            </a>
          </div>
          <div className="flex gap-3 mt-7">
            <button
              data-testid="footer-privacy-policy"
              onClick={() => setPolicy("privacy")}
              className="text-xs border border-[#F6F1EC]/25 rounded-full px-4 py-2 text-[#F6F1EC]/75 hover:text-white hover:border-[#F6F1EC]/50 transition-colors"
            >
              Privacy Policy
            </button>
            <button
              data-testid="footer-medical-disclaimer"
              onClick={() => setPolicy("disclaimer")}
              className="text-xs border border-[#F6F1EC]/25 rounded-full px-4 py-2 text-[#F6F1EC]/75 hover:text-white hover:border-[#F6F1EC]/50 transition-colors"
            >
              Medical Disclaimer
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-[#F6F1EC]/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#F6F1EC]/60">
            © 2026 Dr. Ojaswini Sheshrao Gajbhiye. All rights reserved.
          </p>
          <p className="text-xs text-[#F6F1EC]/60 text-center sm:text-right max-w-xl">
            Information on this website is intended for general educational purposes and does not replace an
            individual medical consultation.
          </p>
        </div>
      </div>

      <AnimatePresence>
        {policy && (
          <motion.div
            data-testid="footer-policy-modal"
            className="fixed inset-0 z-[80] flex items-center justify-center p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-[#383237]/60 backdrop-blur-sm" onClick={() => setPolicy(null)} />
            <motion.div
              className="relative bg-[#FAF7F4] text-[#383237] rounded-3xl max-w-lg w-full p-8 sm:p-10 shadow-soft"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              role="dialog"
              aria-modal="true"
              aria-label={POLICIES[policy].title}
            >
              <button
                data-testid="footer-policy-close"
                onClick={() => setPolicy(null)}
                aria-label="Close"
                className="absolute top-5 right-5 p-2 text-[#8A7E88] hover:text-[#70435F] transition-colors"
              >
                <X size={20} />
              </button>
              <h3 className="font-display text-2xl font-semibold text-[#70435F] mb-4">{POLICIES[policy].title}</h3>
              <p className="text-sm text-[#695E67] leading-relaxed">{POLICIES[policy].body}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
};

export default Footer;
